import React from 'react';
import { boxShade } from './BoxDistributionBar';
import './MethodDistributionBar.css';

/**
 * Одна полоса на любую методику.
 *
 * Сегменты приходят подписанными ключами, а не номерами боксов: у лестницы это коробки,
 * у адаптивной — сроки, у критерия — число верных. Компонент про них ничего не знает и
 * знать не должен — он рисует доли и подписи, а смысл шкалы задаёт тот, кто её собрал.
 * Оттенки берутся с той же шкалы, что у полосы боксов, чтобы две полосы на одной
 * странице не выглядели из разных приложений.
 *
 * Единственное исключение — корзина «не начато»: её ключ передают отдельно, и она
 * выпадает из шкалы совсем. Шкала идёт от светлого к тёмному и читается как «слабое →
 * выученное», а не начатое стоит в ней последним — то есть красилось в самый насыщенный
 * цвет и выглядело как самое выученное. Ровно из-за этой ошибки — «не начато» на вид
 * как «готово» — страницу выбора практики и переделывали.
 */
const MethodDistributionBar = ({ buckets, labelFor, emptyLabel, notStartedKey }) => {
    const total = (buckets ?? []).reduce((sum, bucket) => sum + (bucket.count || 0), 0);

    if (!buckets?.length || total === 0) {
        return <div className="methodBar methodBar--empty" role="img" aria-label={emptyLabel} />;
    }

    // Шкала строится по корзинам, у которых есть порядок; «не начато» вне его.
    const scaled = buckets.filter((bucket) => bucket.key !== notStartedKey);
    const isNotStarted = (bucket) => bucket.key === notStartedKey;
    const shadeOf = (bucket) => boxShade(scaled.indexOf(bucket), scaled.length);

    const description = buckets
        .filter((bucket) => bucket.count > 0)
        .map((bucket) => `${labelFor(bucket.key)}: ${bucket.count}`)
        .join(', ');

    return (
        <>
            <div className="methodBar" role="img" aria-label={description}>
                {buckets.map((bucket) => (
                    bucket.count > 0 && (
                        <span
                            key={bucket.key}
                            data-testid="method-bar-seg"
                            className={isNotStarted(bucket)
                                ? 'methodBar__seg methodBar__seg--notStarted'
                                : 'methodBar__seg'}
                            style={{
                                flexGrow: bucket.count,
                                backgroundColor: isNotStarted(bucket) ? undefined : shadeOf(bucket),
                            }}
                        />
                    )
                ))}
            </div>
            <div className="methodLegend">
                {buckets.map((bucket) => (
                    <span className="methodLegend__item" key={bucket.key}>
                        <span
                            className={isNotStarted(bucket)
                                ? 'methodLegend__swatch methodLegend__swatch--notStarted'
                                : 'methodLegend__swatch'}
                            style={{ backgroundColor: isNotStarted(bucket) ? undefined : shadeOf(bucket) }}
                        />
                        {/* Обрезается подпись, а не число: колонка легенды фиксированной
                            ширины, и на длинном языке текст в неё может не влезть. Число
                            при этом — то, ради чего на легенду смотрят. */}
                        <span className="methodLegend__label">{labelFor(bucket.key)}</span>
                        <b className="methodLegend__count">{bucket.count}</b>
                    </span>
                ))}
            </div>
        </>
    );
};

export default MethodDistributionBar;
